import React from "react";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { DownloadCardSection } from "@/.storyblok/types/319751/storyblok-components";
import Image from "next/image";
import { renderRichText } from "@/lib/richText";

interface DownloadCardIndustryReportsProps {
  blok?: DownloadCardSection & SbBlokData;
}

const DownloadCardIndustryReports: React.FC<DownloadCardIndustryReportsProps> = ({ blok }) => {
  if (!blok) return null;

  const { type, items_manual, title, description } = blok;

  const renderFeaturedCard = (item: any) => (
    <div className="col mb-3">
      <div className="card card-lighter overflow-hidden h-100 flex-xl-row">
        {item.image?.filename && (
          <Image
            src={item.image.filename}
            alt={item.title}
            width={0}
            height={0}
            className="img-fluid mx-auto component-cards-content-image"
            style={{ width: '100%', height: 'auto' }}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            loading="lazy"
            quality={75}
          />
        )}
        <div className="p-4">
          <h3 className="mt-2">{item.title}</h3>
          <p className="lead mb-0">{item.subtitle}</p>
          <p className="mt-4 mb-0">
            <a
              className="button button-medium button-primary"
              href={item.cta_button_link?.cached_url || ""}
              target="_blank"
            >
              {item.cta_button || 'Download now'}
            </a>
          </p>
        </div>
      </div>
    </div>
  );

  const renderDefaultCard = (item: any) => (
    <div className="one-quarter margin-bottom-medium">
      <div className="resource-preview">
        {item.image?.filename && (
          <Image
            src={item.image.filename}
            alt={item.title}
            width={0}
            height={0}
            className="margin-bottom-small img-fluid"
            style={{ width: '100%', height: 'auto' }}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            loading="lazy"
            quality={75}
          />
        )}
        <h3 className="highlight-text">{item.title}</h3>
        <p className="margin-bottom-small img-fluid">{item.subtitle}</p>
        <a
          className="button button-medium button-secondary-outline"
          href={item.cta_button_link?.cached_url || ""}
        >
          {item.cta_button || 'Download now'}
        </a>
      </div>
    </div>
  );

  const renderCareerCard = (item: any) => (
    <div className="col-24 col-md-8">
      <div className="card card-lighter p-4">
        <h4 className="mb-4">{item.title}</h4>
        {item.description && (
          <div className="mb-4"
               dangerouslySetInnerHTML={{ __html: renderRichText(item.description) }} />
        )}
        <a
          href={item.cta_button_link?.cached_url || ""}
          className="careers btn btn-primary"
          target="_blank"
        >
          {item.cta_button || 'Read More'}
        </a>
      </div>
    </div>
  );

  if (type === 'career') {
    return (
      <div className="container">
        <div className="padding-large">
          <div className="full-width margin-bottom-small">
            <h2 className="text-center">{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <div className="full-width">
            <div className="padding-medium">
              <div className="row g-4">
                {items_manual?.map((item, index) => (
                  <React.Fragment key={item._uid || index}>
                    {renderCareerCard(item)}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'featured') {
    return (
      <div className="bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-20 col-xl-18 my-5">
              <div className="bg-light component-cards-content py-4 py-md-5">
                <div className="container row row-cols-1 row-cols-lg-1 justify-content-center g-lg-4 mx-auto">
                  {items_manual?.map((item, index) => (
                    <React.Fragment key={item._uid || index}>
                      {renderFeaturedCard(item)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...storyblokEditable(blok)} className="container">
      <div className="padding-medium">
        <div className="full-width margin-bottom-medium">
          <h2 className="text-center">{title}</h2>
          {description && (
            <p className="text-center">{description}</p>
          )}
        </div>
        <div className="full-width margin-bottom-medium">
          {items_manual?.map((item, index) => (
            <React.Fragment key={item._uid || index}>
              {renderDefaultCard(item)}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DownloadCardIndustryReports;